import React from 'react';
import { useSprings, animated, useSpring } from 'react-spring'
import { useDrag } from 'react-use-gesture'
import clamp from 'lodash-es/clamp'
import * as styles from './Carousel.module.scss';
import classnames from 'classnames';

type Props = {
    children: Array<any>;
    topCarouselHeight?: number | string;
    subPages?: Array<any>;
    startIndex?: number;
    variant?: "fullwidth" | "normal";
}

export default function Carousel (props: Props) {
    const {children, subPages} = props;
    const wrapperElem = React.useRef();
    const [wrapperWidth, setWrapperWidth]: [number, Function] = React.useState(400);
     

    React.useLayoutEffect(() => {
        if (wrapperElem) {
            setWrapperWidth(wrapperElem.current.clientWidth);
        }
    }, [wrapperElem.current])

    const startIndex: number =  children.length > 1 ? (props.startIndex >= 0) ? props.startIndex : 1 : 0;
    const variant = (props.variant) ? props.variant : "normal";

    const index: any = React.useRef<number>(startIndex);
    const [dindex, setDindex] = React.useState(startIndex);

    const [propsSpring, setSs] = useSprings(children.length, i => ({ x: i * 100, sc: 0.85, display: 'block', opacity: 0 }));

    // The Wrapper of the springs
    const [sliderSpring, setS] = useSpring((i: number) => ({x: i *  100}));

    // Drag and Drop functionality
    const bind = useDrag(({ event, down, delta: [xDelta], direction: [xDir], distance, cancel, canceled }) => {
        event.stopPropagation();
        event.preventDefault();

        if (canceled) {
            return;
        }
        
        if (down && distance > wrapperWidth / 3) {
            cancel();
            index.current = clamp(index.current + (xDir > 0 ? -1 : 1), 0, children.length - 1);
            setDindex(index.current);
        }
          
        setSs.start(i => {
            const fullScale = (index.current === i) ? 1 : 0.85
            const x = (i - index.current) * window.innerWidth + (down ? xDelta : 0)
            const sc = down ? 1 - distance / wrapperWidth / 3 : fullScale;
            const opacity = (i === index.current) ? 1 : 0;
            return { sc, opacity, x };
        });


        setS.start(i => {
            const x = (i - index.current) * 100 / children.length + (down ? xDelta : 0);
            return { x };
        });
      });


      // This updates the carousel if it changes from outside by controlls for example.
      React.useEffect(() => {
        setSs.start((i: number) => {
            const fullScale = (index.current === i) ? 1 : 0.85
            const sc = fullScale;
            const opacity = (i === index.current) ? 1 : 0;
            return {sc, opacity};
        });

        setS.start((i: number) => {
            const x = (i - index.current) * 100 / children.length;
            return {x};
        })
      }, [dindex]);
    
    return <div className={classnames(styles.componentWrapper)}>
        <div ref={wrapperElem} className={classnames([(variant === "normal") ? styles.wrapper : styles.wrapperFullwidth])}>

            <animated.div {...bind()} className={styles.itemWrapper} style={{width: 100 * children.length + "%", transform: sliderSpring.x.to(x => `translate3d(${x}%,0,0)`)}}>

                 {propsSpring.map(({ sc }, i: number) => {
                    return <animated.div key={i} className={styles.item}>
                        <animated.div style={{ transform: sc.to(s => `scale(${s})`) }} >
                            {children[i]}
                        </animated.div>
                    </animated.div>
                })}

            </animated.div>

           

        </div>

        {children.length > 1 && <div className="has-text-centered is-size-4">
                {children.map((item, indexToSet) => {
                    return <a key={indexToSet} onClick={() => { index.current = indexToSet; setDindex(indexToSet)}}>
                        <span className="icon is-medium">
                            {(indexToSet === dindex) ?<span className={classnames([styles.radio, styles.radioOn])}></span> : <span className={classnames([styles.radio])}></span>}
                        </span>
                    </a>
                })}
            </div>}
        
       
        {subPages && <div ref={wrapperElem} className={classnames([styles.wrapper, children.length <= 1 && styles.wrapperGap])}>

            <animated.div {...bind()} className={styles.itemWrapper} style={{width: 100 * children.length + "%", transform: sliderSpring.x.to(x => `translate3d(${x}%,0,0)`)}}>

                {propsSpring.map(({ sc, opacity }, i: number) => {
                    return <animated.div key={i} className={styles.item}>
                    <animated.div style={{ transform: sc.to(s => `scale(${s})`), opacity: opacity }} >
                        {subPages[i]}
                    </animated.div>
                </animated.div>
                })}

            </animated.div>

        </div>}


    </div>
}